import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  GET_TENANT_SUBSCRIPTION,
  GRANT_TENANT_SUBSCRIPTION,
  CANCEL_TENANT_SUBSCRIPTION,
  GET_SUBSCRIPTION_PLANS,
  GET_TENANT_INVOICES,
  LOG_TENANT_INVOICE,
  LOG_TENANT_INVOICE_PAYMENT,
  DELETE_TENANT_INVOICE,
  DELETE_TENANT_INVOICE_PAYMENT,
} from '../graphql/billing';
import { TenantSubscription, SubscriptionPlan, TenantInvoice } from '../types';
import { useUserRole } from '../hooks/useUserRole';

interface TenantSubscriptionPanelProps {
  tenantId: string;
  tenantName: string;
  open: boolean;
  onClose: () => void;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    Active: { label: 'Active', color: 'success' },
    Pending: { label: 'Pending', color: 'warning' },
    Expired: { label: 'Expired', color: 'error' },
    Cancelled: { label: 'Cancelled', color: 'default' },
  };
  const cfg = map[status] ?? { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600 }} />;
}

function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

function PricingSummary({
  usersIncluded,
  costPerUser,
  taxRatePercent,
  processingFeeAmount,
}: {
  usersIncluded: number;
  costPerUser: number;
  taxRatePercent: number;
  processingFeeAmount: number;
}) {
  const subtotal = costPerUser * usersIncluded;
  const tax = subtotal * (taxRatePercent / 100);
  const total = subtotal + tax + processingFeeAmount;

  const rows: Array<{ label: string; value: string; bold?: boolean; divider?: boolean }> = [
    { label: 'Subtotal', value: formatCurrency(subtotal), divider: true },
    { label: `Tax (${taxRatePercent.toFixed(2)}%)`, value: formatCurrency(tax) },
    { label: 'Processing / handling fee', value: formatCurrency(processingFeeAmount) },
    { label: 'Total', value: formatCurrency(total), bold: true, divider: true },
  ];

  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          letterSpacing: 0.5,
          px: 2,
          py: 1,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          textTransform: 'uppercase',
        }}
      >
        Pricing Summary
      </Typography>
      {rows.map((row) => (
        <Box key={row.label}>
          {row.divider && <Divider />}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 0.75,
            }}
          >
            <Typography
              variant="body2"
              color={row.bold ? 'text.primary' : 'text.secondary'}
              fontWeight={row.bold ? 700 : 400}
            >
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={row.bold ? 700 : 500}
              color={row.bold ? 'primary.main' : 'text.primary'}
            >
              {row.value}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function invoiceStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status) {
    case 'Paid': return 'success';
    case 'PartiallyPaid': return 'info';
    case 'Sent': return 'default';
    case 'Overdue': return 'error';
    case 'Void': return 'default';
    default: return 'warning';
  }
}

function InvoiceSection({
  invoices,
  onLogInvoice,
  onLogPayment,
  onDeleteInvoice,
  onDeletePayment,
}: {
  invoices: TenantInvoice[];
  onLogInvoice: () => void;
  onLogPayment: (invoiceId: string) => void;
  onDeleteInvoice: (invoiceId: string, invoiceNumber: string) => void;
  onDeletePayment: (invoiceId: string, paymentId: string) => void;
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ReceiptIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700}>Invoices</Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onLogInvoice}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          variant="outlined"
        >
          Log Invoice
        </Button>
      </Box>

      {invoices.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No invoices logged yet.
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {invoices.map((inv, idx) => {
            const balance = inv.amount - inv.amountPaid;
            return (
              <Box key={inv.id}>
                {idx > 0 && <Divider />}
                <Box sx={{ px: 2, py: 1.5 }}>
                  {/* Invoice header row */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={700}>{inv.invoiceNumber}</Typography>
                        <Chip
                          label={inv.status === 'PartiallyPaid' ? 'Partial' : inv.status}
                          color={invoiceStatusColor(inv.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Issued {formatDate(inv.issuedDate)} · Due {formatDate(inv.dueDate)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
                        <Typography variant="caption">
                          Total: <strong>{formatCurrency(inv.amount)}</strong>
                        </Typography>
                        <Typography variant="caption">
                          Paid: <strong>{formatCurrency(inv.amountPaid)}</strong>
                        </Typography>
                        {balance > 0.001 && (
                          <Typography variant="caption" color="error.main">
                            Balance: <strong>{formatCurrency(balance)}</strong>
                          </Typography>
                        )}
                      </Box>
                      {inv.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {inv.notes}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      {inv.status !== 'Paid' && inv.status !== 'Void' && (
                        <Button
                          size="small"
                          startIcon={<PaymentIcon />}
                          onClick={() => onLogPayment(inv.id)}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                          variant="text"
                        >
                          Log Payment
                        </Button>
                      )}
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => onDeleteInvoice(inv.id, inv.invoiceNumber)}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                        color="error"
                        variant="text"
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>

                  {/* Payment rows */}
                  {inv.payments.length > 0 && (
                    <Box sx={{ mt: 1, ml: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                      {inv.payments.map((pmt) => (
                        <Box
                          key={pmt.id}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
                        >
                          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
                            {formatDate(pmt.paymentDate)} · <strong>{formatCurrency(pmt.amount)}</strong> · {pmt.method.replace('_', ' ')}
                            {pmt.referenceNumber ? ` · Ref: ${pmt.referenceNumber}` : ''}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => onDeletePayment(inv.id, pmt.id)}
                            sx={{ minWidth: 0, p: 0.25, color: 'error.main' }}
                            title="Delete payment"
                          >
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

const TenantSubscriptionPanel: React.FC<TenantSubscriptionPanelProps> = ({  tenantId,
  tenantName,
  open,
  onClose,
}) => {
  const { user } = useUserRole();
  const isSuperAdmin = user?.roleId === 1;

  // Subscription query
  const { data, loading, refetch } = useQuery<{ tenantSubscription: TenantSubscription | null }>(
    GET_TENANT_SUBSCRIPTION,
    { variables: { tenantId }, skip: !open || !tenantId },
  );
  const sub = data?.tenantSubscription;

  // Institutional plan query (for pricing breakdown)
  const { data: plansData } = useQuery<{ subscriptionPlans: SubscriptionPlan[] }>(
    GET_SUBSCRIPTION_PLANS,
    { skip: !open },
  );
  const institutionalPlan = plansData?.subscriptionPlans?.find(
    (p) => p.planId === 'institutional',
  );

  // Invoice query (super-admin only)
  const { data: invoicesData, refetch: refetchInvoices } = useQuery<{ tenantInvoices: TenantInvoice[] }>(
    GET_TENANT_INVOICES,
    { variables: { tenantId }, skip: !open || !tenantId || !isSuperAdmin },
  );
  const invoices = invoicesData?.tenantInvoices ?? [];

  // Grant dialog state
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantStartDate, setGrantStartDate] = useState('');
  const [grantExpiryDate, setGrantExpiryDate] = useState('');
  const [grantRef, setGrantRef] = useState('');
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantUsersIncluded, setGrantUsersIncluded] = useState(10000);
  const [grantCostPerUser, setGrantCostPerUser] = useState(5.00);
  const [grantTaxRate, setGrantTaxRate] = useState(0);
  const [grantFee, setGrantFee] = useState(0);

  // Cancel dialog state
  const [cancelOpen, setCancelOpen] = useState(false);

  // Log invoice dialog state
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceIssuedDate, setInvoiceIssuedDate] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  // Log payment dialog state
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('wire');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<string | null>(null);
  const [deleteInvoiceNumber, setDeleteInvoiceNumber] = useState('');
  const [deletePaymentTarget, setDeletePaymentTarget] = useState<{ invoiceId: string; paymentId: string } | null>(null);

  // Snackbar
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [grantSub, { loading: grantLoading }] = useMutation<{
    grantTenantSubscription: TenantSubscription;
  }>(GRANT_TENANT_SUBSCRIPTION);

  const [cancelSub, { loading: cancelLoading }] = useMutation(CANCEL_TENANT_SUBSCRIPTION);

  const [logInvoice, { loading: invoiceLoading }] = useMutation(LOG_TENANT_INVOICE);
  const [logPayment, { loading: paymentLoading }] = useMutation(LOG_TENANT_INVOICE_PAYMENT);
  const [deleteInvoiceMutation, { loading: deleteInvoiceLoading }] = useMutation(DELETE_TENANT_INVOICE);
  const [deletePaymentMutation, { loading: deletePaymentLoading }] = useMutation(DELETE_TENANT_INVOICE_PAYMENT);

  const openGrantDialog = (prefill?: { start?: string; expiry?: string }) => {
    const today = new Date().toISOString().split('T')[0];
    const nextYear = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setGrantStartDate(prefill?.start ?? today);
    setGrantExpiryDate(prefill?.expiry ?? nextYear);
    setGrantRef('');
    setGrantError(null);
    // Seed pricing from plan defaults
    setGrantUsersIncluded(institutionalPlan?.usersIncluded ?? 1000);
    setGrantCostPerUser(institutionalPlan?.costPerUser ?? 5.00);
    setGrantTaxRate(institutionalPlan?.taxRatePercent ?? 0);
    setGrantFee(institutionalPlan?.processingFeeAmount ?? 0);
    setGrantOpen(true);
  };

  const handleGrant = async () => {
    if (!grantStartDate || !grantExpiryDate) return;
    setGrantError(null);
    try {
      await grantSub({
        variables: {
          tenantId,
          planId: 'institutional',
          startDate: new Date(grantStartDate + 'T00:00:00').toISOString(),
          expiryDate: new Date(grantExpiryDate + 'T23:59:59').toISOString(),
          referenceNote: grantRef || null,
          usersIncluded: grantUsersIncluded,
          costPerUser: grantCostPerUser,
          taxRatePercent: grantTaxRate,
          processingFeeAmount: grantFee,
        },
      });
      setGrantOpen(false);
      await refetch();
      setSuccessMsg('Institutional subscription granted successfully.');
    } catch (err: any) {
      setGrantError(err.message || 'Failed to grant subscription.');
    }
  };

  const handleCancel = async () => {
    setCancelOpen(false);
    try {
      await cancelSub({ variables: { tenantId } });
      await refetch();
      setSuccessMsg('Subscription cancelled.');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to cancel subscription.');
    }
  };

  const openInvoiceDialog = () => {
    const today = new Date().toISOString().split('T')[0];
    const net30 = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setInvoiceNumber('');
    setInvoiceAmount('');
    setInvoiceIssuedDate(today);
    setInvoiceDueDate(net30);
    setInvoiceNotes('');
    setInvoiceError(null);
    setInvoiceOpen(true);
  };

  const handleLogInvoice = async () => {
    setInvoiceError(null);
    if (!invoiceNumber.trim() || !invoiceAmount || !invoiceIssuedDate || !invoiceDueDate) {
      setInvoiceError('Invoice number, amount, issued date, and due date are required.');
      return;
    }
    try {
      await logInvoice({
        variables: {
          tenantId,
          subscriptionId: sub?.id ?? null,
          invoiceNumber: invoiceNumber.trim(),
          amount: parseFloat(invoiceAmount),
          issuedDate: new Date(invoiceIssuedDate + 'T00:00:00').toISOString(),
          dueDate: new Date(invoiceDueDate + 'T23:59:59').toISOString(),
          notes: invoiceNotes || null,
        },
      });
      setInvoiceOpen(false);
      await refetchInvoices();
      setSuccessMsg(`Invoice ${invoiceNumber} logged.`);
    } catch (err: any) {
      setInvoiceError(err.message || 'Failed to log invoice.');
    }
  };

  const openPaymentDialog = (invoiceId: string) => {
    setPaymentInvoiceId(invoiceId);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentAmount('');
    setPaymentMethod('wire');
    setPaymentRef('');
    setPaymentNotes('');
    setPaymentError(null);
    setPaymentOpen(true);
  };

  const handleLogPayment = async () => {
    setPaymentError(null);
    if (!paymentAmount || !paymentDate) {
      setPaymentError('Payment date and amount are required.');
      return;
    }
    try {
      await logPayment({
        variables: {
          invoiceId: paymentInvoiceId,
          paymentDate: new Date(paymentDate + 'T00:00:00').toISOString(),
          amount: parseFloat(paymentAmount),
          method: paymentMethod,
          referenceNumber: paymentRef || null,
          notes: paymentNotes || null,
        },
      });
      setPaymentOpen(false);
      await refetchInvoices();
      setSuccessMsg('Payment logged.');
    } catch (err: any) {
      setPaymentError(err.message || 'Failed to log payment.');
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteInvoiceId) return;
    try {
      await deleteInvoiceMutation({ variables: { invoiceId: deleteInvoiceId } });
      setDeleteInvoiceId(null);
      await refetchInvoices();
      setSuccessMsg(`Invoice ${deleteInvoiceNumber} deleted.`);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to delete invoice.');
      setDeleteInvoiceId(null);
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentTarget) return;
    try {
      await deletePaymentMutation({
        variables: { invoiceId: deletePaymentTarget.invoiceId, paymentId: deletePaymentTarget.paymentId },
      });
      setDeletePaymentTarget(null);
      await refetchInvoices();
      setSuccessMsg('Payment removed.');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to delete payment.');
      setDeletePaymentTarget(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsIcon sx={{ color: 'primary.main' }} />
            <span>Organisation Subscription</span>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {tenantName}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Divider sx={{ mb: 2.5 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : sub && sub.status === 'Active' ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Plan</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <GroupsIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                    <Typography variant="body2" fontWeight={600}>Institutional</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip status={sub.status} />
                  </Box>
                </Grid>

                {sub.startDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Started</Typography>
                    <Typography variant="body2" fontWeight={500}>{formatDate(sub.startDate)}</Typography>
                  </Grid>
                )}

                {sub.expiryDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Expires</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight={500}>{formatDate(sub.expiryDate)}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {institutionalPlan && (
                <PricingSummary
                  usersIncluded={institutionalPlan.usersIncluded}
                  costPerUser={institutionalPlan.costPerUser}
                  taxRatePercent={institutionalPlan.taxRatePercent}
                  processingFeeAmount={institutionalPlan.processingFeeAmount}
                />
              )}

              {isSuperAdmin && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => openGrantDialog({
                      start: sub.startDate?.split('T')[0],
                      expiry: sub.expiryDate?.split('T')[0],
                    })}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                  >
                    Modify Grant
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => setCancelOpen(true)}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Cancel Subscription
                  </Button>
                </Box>
              )}

              {isSuperAdmin && (
                <InvoiceSection
                  invoices={invoices}
                  onLogInvoice={openInvoiceDialog}
                  onLogPayment={openPaymentDialog}
                  onDeleteInvoice={(id, num) => { setDeleteInvoiceId(id); setDeleteInvoiceNumber(num); }}
                  onDeletePayment={(invoiceId, paymentId) => setDeletePaymentTarget({ invoiceId, paymentId })}
                />
              )}
            </>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {sub
                  ? `Subscription is currently ${sub.status.toLowerCase()}.`
                  : 'No active subscription for this organisation.'}
              </Typography>
              {isSuperAdmin && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => openGrantDialog()}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Grant Institutional Subscription
                </Button>
              )}

              {institutionalPlan && (
                <PricingSummary
                  usersIncluded={institutionalPlan.usersIncluded}
                  costPerUser={institutionalPlan.costPerUser}
                  taxRatePercent={institutionalPlan.taxRatePercent}
                  processingFeeAmount={institutionalPlan.processingFeeAmount}
                />
              )}

              {isSuperAdmin && invoices.length > 0 && (
                <InvoiceSection
                  invoices={invoices}
                  onLogInvoice={openInvoiceDialog}
                  onLogPayment={openPaymentDialog}
                  onDeleteInvoice={(id, num) => { setDeleteInvoiceId(id); setDeleteInvoiceNumber(num); }}
                  onDeletePayment={(invoiceId, paymentId) => setDeletePaymentTarget({ invoiceId, paymentId })}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Grant / Modify dialog */}
      <Dialog
        open={grantOpen}
        onClose={() => !grantLoading && setGrantOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {sub?.status === 'Active' ? 'Modify Grant' : 'Grant Institutional Subscription'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Manually activate an institutional subscription for <strong>{tenantName}</strong>.
            Payment is handled offline (invoice, purchase order, wire transfer, etc.).
          </DialogContentText>
          {grantError && (
            <Alert severity="error" sx={{ mb: 2 }}>{grantError}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={grantStartDate}
              onChange={(e) => setGrantStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={grantLoading}
            />
            <TextField
              label="Expiry Date"
              type="date"
              value={grantExpiryDate}
              onChange={(e) => setGrantExpiryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={grantLoading}
            />
            <TextField
              label="Reference Note (Invoice #, PO, etc.)"
              value={grantRef}
              onChange={(e) => setGrantRef(e.target.value)}
              placeholder="e.g. INV-2026-001"
              fullWidth
              size="small"
              disabled={grantLoading}
            />
            <Divider />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Users Included"
                type="number"
                value={grantUsersIncluded}
                onChange={(e) => setGrantUsersIncluded(Math.max(1, Number(e.target.value)))}
                inputProps={{ min: 1, step: 1 }}
                size="small"
                disabled={grantLoading}
              />
              <TextField
                label="Cost Per User (USD)"
                type="number"
                value={grantCostPerUser}
                onChange={(e) => setGrantCostPerUser(Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0, step: 0.01 }}
                size="small"
                disabled={grantLoading}
              />
              <TextField
                label="Tax Rate (%)"
                type="number"
                value={grantTaxRate}
                onChange={(e) => setGrantTaxRate(Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0, step: 0.01 }}
                size="small"
                disabled={grantLoading}
              />
              <TextField
                label="Processing Fee (USD)"
                type="number"
                value={grantFee}
                onChange={(e) => setGrantFee(Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0, step: 0.01 }}
                size="small"
                disabled={grantLoading}
              />
            </Box>
            <PricingSummary
              usersIncluded={grantUsersIncluded}
              costPerUser={grantCostPerUser}
              taxRatePercent={grantTaxRate}
              processingFeeAmount={grantFee}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGrantOpen(false)}
            sx={{ textTransform: 'none' }}
            disabled={grantLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGrant}
            disabled={grantLoading || !grantStartDate || !grantExpiryDate}
            startIcon={grantLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {grantLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Cancel organisation subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All users in <strong>{tenantName}</strong> will lose their subscription access.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>
            Keep subscription
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelLoading}
            onClick={handleCancel}
            sx={{ textTransform: 'none' }}
          >
            {cancelLoading ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Invoice confirmation */}
      <Dialog
        open={!!deleteInvoiceId}
        onClose={() => !deleteInvoiceLoading && setDeleteInvoiceId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteInvoiceNumber}</strong> and all its payment records? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteInvoiceId(null)} sx={{ textTransform: 'none' }} disabled={deleteInvoiceLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteInvoice}
            disabled={deleteInvoiceLoading}
            startIcon={deleteInvoiceLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleteInvoiceLoading ? 'Deleting…' : 'Delete Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Payment confirmation */}
      <Dialog
        open={!!deletePaymentTarget}
        onClose={() => !deletePaymentLoading && setDeletePaymentTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this payment record from the invoice? The invoice balance and status will be recalculated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePaymentTarget(null)} sx={{ textTransform: 'none' }} disabled={deletePaymentLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeletePayment}
            disabled={deletePaymentLoading}
            startIcon={deletePaymentLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deletePaymentLoading ? 'Removing…' : 'Remove Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log Invoice dialog */}
      <Dialog
        open={invoiceOpen}
        onClose={() => !invoiceLoading && setInvoiceOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Log Invoice</DialogTitle>
        <DialogContent>
          {invoiceError && <Alert severity="error" sx={{ mb: 2 }}>{invoiceError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Invoice Number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="e.g. INV-2026-001"
              fullWidth size="small" disabled={invoiceLoading}
            />
            <TextField
              label="Amount (USD)"
              type="number"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
              fullWidth size="small" disabled={invoiceLoading}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Issued Date"
                type="date"
                value={invoiceIssuedDate}
                onChange={(e) => setInvoiceIssuedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small" disabled={invoiceLoading}
              />
              <TextField
                label="Due Date"
                type="date"
                value={invoiceDueDate}
                onChange={(e) => setInvoiceDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small" disabled={invoiceLoading}
              />
            </Box>
            <TextField
              label="Notes (optional)"
              value={invoiceNotes}
              onChange={(e) => setInvoiceNotes(e.target.value)}
              multiline rows={2}
              fullWidth size="small" disabled={invoiceLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceOpen(false)} sx={{ textTransform: 'none' }} disabled={invoiceLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLogInvoice}
            disabled={invoiceLoading}
            startIcon={invoiceLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {invoiceLoading ? 'Saving…' : 'Log Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log Payment dialog */}
      <Dialog
        open={paymentOpen}
        onClose={() => !paymentLoading && setPaymentOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Log Payment</DialogTitle>
        <DialogContent>
          {paymentError && <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth size="small" disabled={paymentLoading}
            />
            <TextField
              label="Amount (USD)"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
              fullWidth size="small" disabled={paymentLoading}
            />
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              size="small"
              fullWidth
              disabled={paymentLoading}
            >
              <MenuItem value="wire">Wire Transfer</MenuItem>
              <MenuItem value="ach">ACH</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="credit_card">Credit Card</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            <TextField
              label="Reference Number (optional)"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="Check #, wire reference, etc."
              fullWidth size="small" disabled={paymentLoading}
            />
            <TextField
              label="Notes (optional)"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              multiline rows={2}
              fullWidth size="small" disabled={paymentLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)} sx={{ textTransform: 'none' }} disabled={paymentLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLogPayment}
            disabled={paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {paymentLoading ? 'Saving…' : 'Log Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success snackbar */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={5000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ width: '100%' }}>
          {successMsg}
        </Alert>
      </Snackbar>

      {/* Error snackbar */}
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TenantSubscriptionPanel;
